import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React, { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router";
import AuthCallback from "./app/AuthCallback";
import Landing from "./app/Landing";
import Presentation from "./app/Presentation";
import Report from "./app/Report";
import ReportDebug from "./app/ReportDebug";
import ReportEdit from "./app/ReportEdit";
import Reports from "./app/Reports";
import Settings from "./app/Settings";
import Test from "./app/Test";
import TestAsync from "./app/TestAsync";
import Auth0ProviderWithConfig from "./auth/AuthProvider";
import AuthSetup from "./auth/AuthSetup";
import AxiosInterceptor from "./auth/AxiosInterceptor";
import ProtectedRoute from "./auth/ProtectedRoute";
import DefaultErrorBoundary from "./components/ErrorBoundary";
import ThemeProvider from "./components/ThemeProvider";
import { AppProvider } from "./context/AppContext";
import "./index.css";
import { initBugsnag } from "./utils/bugsnag";
import { initMixpanel } from "./utils/mixpanel";
import Builder from "./app/Builder/Builder";

const bugsnagClient = initBugsnag();
initMixpanel();
let ErrorBoundary = DefaultErrorBoundary;
if (bugsnagClient) {
  const BugsnagPluginReact = bugsnagClient.getPlugin("react");
  if (BugsnagPluginReact) {
    ErrorBoundary = BugsnagPluginReact.createErrorBoundary(React);
  }
}

const enableMocks = async () => {
  if (import.meta.env.VITE_ENABLE_MSW === "true" && import.meta.env.DEV) {
    const { worker } = await import("./api/mocks/browser");
    worker.start();
  }
};

const queryClient = new QueryClient();

enableMocks().then(() => {
  createRoot(document.getElementById("root")).render(
    <StrictMode>
      <ErrorBoundary>
        <Auth0ProviderWithConfig>
          <AxiosInterceptor />
          <QueryClientProvider client={queryClient}>
            <AppProvider>
              <AuthSetup>
                <ThemeProvider>
                  <BrowserRouter>
                    <Routes>
                      <Route path="/callback" element={<AuthCallback />} />
                      <Route path="/v1">
                        <Route
                          index
                          element={
                            <ProtectedRoute>
                              <Landing />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="report"
                          element={
                            <ProtectedRoute>
                              <Report />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="report/:id"
                          element={
                            <ProtectedRoute>
                              <Report />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="report/:id/edit"
                          element={
                            <ProtectedRoute>
                              <ReportEdit />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="report/:id/debug"
                          element={
                            <ProtectedRoute>
                              <ReportDebug />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="reports"
                          element={
                            <ProtectedRoute>
                              <Reports />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="settings"
                          element={
                            <ProtectedRoute>
                              <Settings />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="test"
                          element={
                            <ProtectedRoute>
                              <Test />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="test-async"
                          element={
                            <ProtectedRoute>
                              <TestAsync />
                            </ProtectedRoute>
                          }
                        />
                      </Route>
                      <Route
                        path="/"
                        element={
                          <ProtectedRoute>
                            <Builder />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/presentation"
                        element={
                          <ProtectedRoute>
                            <Presentation />
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                  </BrowserRouter>
                </ThemeProvider>
              </AuthSetup>
            </AppProvider>
          </QueryClientProvider>
        </Auth0ProviderWithConfig>
      </ErrorBoundary>
    </StrictMode>
  );
});
